import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { GenericSearchableSelect } from '@/styles/GenericSearchableSelect';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { CancelButton, CloseButton, SaveChangesButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';
import { costCodeLabel, isChildCode, useCostCodes } from '@/lib/cost-codes';
import { searchCatalog, type CatalogSearchRow } from '../../catalog/services/catalog-api';
import { formatMoney } from '@/lib/line-items';
import { comparisonOffers, costCodeText } from '../lib/po-lines';

const ALL = '__all__';
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

interface AddFromCatalogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  vendors: { id: string; name: string }[];
  /** the picked items, to become lines (the caller prices them with the order's vendor) */
  onAdd: (items: CatalogSearchRow[]) => void;
}

/**
 * "Add from Catalog": search the company's catalog by item name, filter by cost code and by vendor (both filters are dropdowns you can
 * also type in), tick any number of items (the ticks stay while paging) and add them all at once. An item may be added again even if it is
 * already on the order: every line is its own. Price 1..3 compare up to three REAL vendor offers per item (the preferred one first, then
 * the cheapest); they only inform -- the line gets the vendor and price the order's own rules choose (po-lines.ts), whatever the order shown.
 * The modal is the app's generic one (same overlay / header / footer classes as every form); the list is the generic table.
 */
export function AddFromCatalogDialog({ isOpen, onClose, vendors, onAdd }: AddFromCatalogDialogProps) {
  const { codes } = useCostCodes(isOpen);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [costCodeFilter, setCostCodeFilter] = useState(ALL);
  const [vendorFilter, setVendorFilter] = useState(ALL);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<{ rows: CatalogSearchRow[]; total: number; hasMore: boolean }>({ rows: [], total: 0, hasMore: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // every row seen so far, by id: a tick made on page 1 must still be an item to add when the user is on page 3
  const seen = useRef(new Map<string, CatalogSearchRow>());

  // a fresh dialog every time it opens
  useEffect(() => {
    if (!isOpen) return;
    setSearchText(''); setSearch(''); setCostCodeFilter(ALL); setVendorFilter(ALL); setPage(1); setSelectedKeys([]); setError(null);
    seen.current = new Map();
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchText); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    searchCatalog({
      search,
      costCodeId: costCodeFilter === ALL ? undefined : costCodeFilter,
      vendorId: vendorFilter === ALL ? undefined : vendorFilter,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then((found) => {
        if (cancelled) return;
        found.rows.forEach((row) => seen.current.set(row.id, row));
        setResult(found);
        setError(null);
      })
      .catch((failure) => { if (!cancelled) { console.error('Error searching the catalog:', failure); setError(failure instanceof Error ? failure.message : 'Failed to search the catalog'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, search, costCodeFilter, vendorFilter, page]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const vendorName = (id: string) => vendors.find((v) => v.id === id)?.name ?? 'Vendor';

  // Price 1..3: the item's first three offers in the stable order (preferred, then cheapest). An item with fewer offers leaves the rest empty.
  const priceColumn = (position: number): GenericTableColumn<CatalogSearchRow> => ({
    key: `price_${position + 1}`,
    header: `Price ${position + 1}`,
    width: '8.5rem',
    render: (row) => {
      const offer = comparisonOffers(row)[position];
      if (!offer) return null;
      return (
        <div data-testid={`price-${position + 1}-${row.id}`}>
          <div className="flex items-center gap-1 tabular-nums">
            {formatMoney(offer.unit_price)}
            {offer.is_preferred && <span title="Preferred vendor" aria-label="Preferred vendor" className="text-gray-900"><Star className="w-3 h-3 fill-current" /></span>}
          </div>
          <div className="text-xs text-gray-500">{vendorName(offer.vendor_id)}</div>
        </div>
      );
    },
  });

  const columns: GenericTableColumn<CatalogSearchRow>[] = [
    {
      key: 'name',
      header: 'Item',
      flexGrow: 2,
      render: (row) => (
        <div>
          <div className="font-medium">{row.name}</div>
          {row.sku && <div className="text-xs text-gray-500">{row.sku}</div>}
        </div>
      ),
    },
    { key: 'cost_code', header: 'Cost Code', width: '10rem', render: (row) => costCodeText(row.cost_code) || '-' },
    { key: 'unit', header: 'Unit', width: '4rem', render: (row) => row.unit || '-' },
    priceColumn(0),
    priceColumn(1),
    priceColumn(2),
  ];

  const add = () => {
    const picked = selectedKeys.map((id) => seen.current.get(id)).filter((row): row is CatalogSearchRow => !!row);
    if (picked.length === 0) return;
    onAdd(picked);
    onClose();
  };

  if (!isOpen) return null;
  return createPortal(
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Add from Catalog" className="generic-form-container" style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }} onClick={(event) => event.stopPropagation()}>
        <div className="generic-form-header generic-form-header-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">Add from Catalog</h2>
          <CloseButton onClick={onClose} />
        </div>

        <div className="generic-form-body generic-form-body-default">
          <div className="space-y-4">
            {error && <div className="generic-form-error" role="alert">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input aria-label="Search by item name" placeholder="Search by item name" value={searchText} onChange={(event) => setSearchText(event.target.value)} className="font-form-input generic-form-field" />
              <GenericSearchableSelect
                id="catalog_filter_cost_code"
                ariaLabel="Filter by cost code"
                value={costCodeFilter === ALL ? '' : costCodeFilter}
                onValueChange={(value) => { setCostCodeFilter(value || ALL); setPage(1); }}
                clearLabel="All cost codes"
                emptyText="No matching cost codes"
                options={codes.filter((c) => c.is_active && isChildCode(c)).map((c) => ({ value: c.id, label: costCodeLabel(c), searchText: `${c.code} ${c.name ?? ''}` }))}
              />
              <GenericSearchableSelect
                id="catalog_filter_vendor"
                ariaLabel="Filter by vendor"
                value={vendorFilter === ALL ? '' : vendorFilter}
                onValueChange={(value) => { setVendorFilter(value || ALL); setPage(1); }}
                clearLabel="All vendors"
                emptyText="No matching vendors"
                options={vendors.map((v) => ({ value: v.id, label: v.name }))}
              />
            </div>

            <GenericTable
              columns={columns}
              data={result.rows}
              loading={loading}
              rowKey={(row) => row.id}
              minTableWidth={820}
              selectable
              selectedKeys={selectedKeys}
              onSelectionChange={setSelectedKeys}
              server={{ total: result.total, mode: 'pages', hasMore: result.hasMore }}
              pageSize={PAGE_SIZE}
              page={page}
              onPageChange={setPage}
              emptyState="No items found"
            />
          </div>
        </div>

        <div className="generic-form-footer generic-form-footer-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          <div className="flex gap-2">
            <CancelButton onClick={onClose}>Cancel</CancelButton>
            <SaveChangesButton type="button" disabled={selectedKeys.length === 0} onClick={add}>Add</SaveChangesButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
